import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';
import { useCallback } from 'react';

import { Alert, AlertType } from '../../components/Alert/Alert';
import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import { Loader } from '../../components/Loader/Loader';
import { Shape2, Shape2Icon, Shape2Theme, Shape2Tone } from '../../components/Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../../components/StyledLink/StyledLink';
import { Bem } from '../../service/Bem/Bem';
import { ValidationErrorData } from '../../typings/common';
import { CanvasAudioGalleryContainer } from '../CanvasAudioGallery/CanvasAudioGalleryContainer';
import { CanvasImageGalleryContainer } from '../CanvasImageGallery/CanvasImageGalleryContainer';
import { CanvasVideoGalleryContainer } from '../CanvasVideoGallery/CanvasVideoGalleryContainer';
import { Page, ResourceType } from '../common/typings';
import { CreativeOptions } from '../CreativeOptions/CreativeOptions';
import { CreativeOptionsConfig, CreativeOptionsData, CreativeOptionsErrors } from '../CreativeOptions/typings';
import { UploadByLinkModalContainer } from '../UploadByLinkModal/UploadByLinkModalContainer';

import './AddCreativePage.css';
import * as keyset from './AddCreativePage.i18n';
import { getEnabledGalleriesFromConfig } from './helpers';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

interface GalleryRendererProps {
    resourceType: ResourceType;
}

const GalleryRenderer = React.memo((props: GalleryRendererProps) => {
    const { resourceType } = props;

    switch (resourceType) {
        case ResourceType.image:
            return <CanvasImageGalleryContainer />;

        case ResourceType.video:
            return <CanvasVideoGalleryContainer />;

        case ResourceType.audio:
            return <CanvasAudioGalleryContainer />;

        default:
            return null;
    }
});

export interface AddCreativePageProps {
    isLoading: boolean;
    isSaving: boolean;
    submitEnabled?: boolean;

    isDocCenterTooltip?: boolean;

    creativeOptions?: CreativeOptionsData;
    onSaveName: (name: string) => void;
    onOptionChange: (groupName: string, name: string, value: string) => void;
    onGroupVisibleChange: (groupName: string) => void;

    preview: React.ReactNode;
    leftPane?: React.ReactNode;
    bottomPane?: React.ReactNode;
    contentCls?: string;
    titleCls?: string;
    optionsCls?: string;
    previewCls?: string;
    iconsTone?: Shape2Tone;

    returnPage?: Page;
    onReturnClick?: (page?: Page) => void;
    onSave: () => void;
    onCancel: () => void;
    error?: ValidationErrorData;
}

interface AddCreativeFooterProps {
    cls?: string;
    progress?: boolean;
    submitEnabled?: boolean;
    returnPage?: Page;
    onSave: () => void;
    onCancel: () => void;
    onReturnClick?: (page?: Page) => void;
}

interface AddCreativePreviewTitleProps {
    titleCls?: string;
    leftPaneEnabled: boolean;
    leftPaneClosed?: boolean;
    rightPaneClosed: boolean;
    iconsTone?: Shape2Tone;
    onToggleRightPaneClose: () => void;
    onToggleLeftPaneClose?: () => void;
}

interface AddCreativePageState {
    rightPaneClosed: boolean;
    leftPaneClosed?: boolean;
}

const BLOCK = 'add-creative-page';

function getReturnText(from: Page): string {
    switch (from) {
        case Page.IDEAS:
            return i18n('back-to-ideas');
        case Page.PRESETS:
            return i18n('back-to-presets');
        case Page.CREATIVES:
            return i18n('back-to-creatives');
        default:
            return '';
    }
}

export class AddCreativePage extends React.PureComponent<AddCreativePageProps, AddCreativePageState> {
    static Footer = React.memo(
        ({ returnPage, progress, submitEnabled, onSave, onCancel, onReturnClick }: AddCreativeFooterProps) => {
            const onClick = useCallback(() => onReturnClick && onReturnClick(returnPage), [returnPage]);

            return (
                <div className={Bem.cls(BLOCK, 'footer')}>
                    <ButtonDecorator
                        cls={Bem.cls(BLOCK, 'submit-button')}
                        theme="action"
                        size="n"
                        text={i18n('create')}
                        view="classic"
                        onClick={onSave}
                        progress={progress}
                        disabled={!submitEnabled}
                    />

                    <ButtonDecorator
                        cls={Bem.cls(BLOCK, 'cancel-button')}
                        theme="normal"
                        size="n"
                        text={i18n('cancel')}
                        view="classic"
                        onClick={onCancel}
                        progress={progress}
                    />

                    {returnPage && onReturnClick && (
                        <ButtonDecorator
                            cls={Bem.cls(BLOCK, 'return-button')}
                            theme="normal"
                            size="n"
                            text={getReturnText(returnPage)}
                            view="classic"
                            onClick={onClick}
                        />
                    )}
                </div>
            );
        }
    );

    static PreviewTitle = React.memo(
        ({
            titleCls,
            iconsTone,
            leftPaneEnabled,
            leftPaneClosed,
            rightPaneClosed,
            onToggleRightPaneClose,
            onToggleLeftPaneClose
        }: AddCreativePreviewTitleProps) => (
            <div className={classNames(Bem.cls(BLOCK, 'preview-title'), titleCls)}>
                {leftPaneEnabled && onToggleLeftPaneClose && (
                    <Shape2
                        icon={Shape2Icon.arrowLeftBig}
                        theme={Shape2Theme.default}
                        tone={iconsTone || Shape2Tone.dark}
                        width={11}
                        height={11}
                        cls={Bem.cls(BLOCK, 'fold-icon', {
                            toggled: Boolean(leftPaneClosed)
                        })}
                        onClick={onToggleLeftPaneClose}
                    />
                )}
                <div className={Bem.cls(BLOCK, 'preview-title-text')}>{i18n('preview-title')}</div>
                <Shape2
                    icon={Shape2Icon.arrowRightBig}
                    theme={Shape2Theme.default}
                    tone={iconsTone || Shape2Tone.dark}
                    width={11}
                    height={11}
                    cls={Bem.cls(BLOCK, 'fold-icon', {
                        toggled: rightPaneClosed
                    })}
                    onClick={onToggleRightPaneClose}
                />
            </div>
        )
    );

    static ErrorLink = React.memo((props: { name: string; title: string; onClick: (name: string) => void }) => {
        const click = useCallback(() => props.onClick(props.name), [props.name]);

        return (
            <StyledLink theme={StyledLinkTheme.alert} onClick={click}>
                {props.title}
            </StyledLink>
        );
    });

    static defaultProps = {
        submitEnabled: true
    };

    state: AddCreativePageState = {
        rightPaneClosed: false
    };

    render(): React.ReactNode {
        const {
            contentCls,
            returnPage,
            onSave,
            onCancel,
            isSaving,
            submitEnabled,
            bottomPane,
            onReturnClick
        } = this.props;

        return (
            <div className={BLOCK}>
                <div className={classNames(Bem.cls(BLOCK, 'content'), contentCls)}>{this.renderContent()}</div>
                {bottomPane || null}
                {this.renderErrors()}
                <AddCreativePage.Footer
                    onSave={onSave}
                    onCancel={onCancel}
                    returnPage={returnPage}
                    progress={isSaving}
                    onReturnClick={onReturnClick}
                    submitEnabled={submitEnabled}
                />
                <UploadByLinkModalContainer />
            </div>
        );
    }

    private errorsKeys(errors: CreativeOptionsErrors): string[] {
        return Object.keys(errors).filter(
            error =>
                errors[error] &&
                Object.keys(errors[error]).some(value => errors[error][value] && errors[error][value].length)
        );
    }

    private getParametersErrors(errors: string[], config: CreativeOptionsConfig) {
        return (
            <div key="parameters-errors">
                {i18nRaw('parameters-errors', {
                    errors: errors.map((error, index) => (
                        <>
                            {index !== 0 && ', '}
                            <AddCreativePage.ErrorLink
                                key={error}
                                name={error}
                                title={config[error].title || config[error].options[0].title}
                                onClick={this.onErrorClick}
                            />
                        </>
                    ))
                })}
            </div>
        );
    }

    private onErrorClick = (name: string) => {
        const error = document.getElementById(`#parameter-id-${name}`);

        if (error) {
            error.scrollIntoView();
        }
    };

    private renderErrors(): React.ReactNode {
        const { creativeOptions, error } = this.props;

        let parametersErrors;
        if (creativeOptions && creativeOptions.errors) {
            parametersErrors = this.errorsKeys(creativeOptions.errors);
        }

        if (!error && !(parametersErrors && parametersErrors.length)) {
            return null;
        }

        return (
            <div>
                <Alert type={AlertType.error}>
                    {error && <div key="error">{error.text}</div>}
                    {parametersErrors &&
                        creativeOptions &&
                        parametersErrors.length &&
                        this.getParametersErrors(parametersErrors, creativeOptions.optionsConfig)}
                </Alert>
            </div>
        );
    }

    private renderContent(): React.ReactNode {
        const {
            creativeOptions,
            onSaveName,
            onOptionChange,
            onGroupVisibleChange,
            preview,
            titleCls,
            optionsCls,
            previewCls,
            iconsTone,
            isLoading,
            leftPane,
            isDocCenterTooltip
        } = this.props;
        const { rightPaneClosed, leftPaneClosed } = this.state;

        if (!creativeOptions) {
            return <Loader size="m" cls={Bem.cls(BLOCK, 'loader')} />;
        }

        const { name, optionsConfig, optionsValue, errors } = creativeOptions;

        return (
            <>
                {isLoading && <Loader size="m" cls={Bem.cls(BLOCK, 'loader')} />}
                {leftPane ? this.renderLeftPane() : null}
                <div className={Bem.cls(BLOCK, 'preview')}>
                    <AddCreativePage.PreviewTitle
                        titleCls={titleCls}
                        iconsTone={iconsTone}
                        leftPaneEnabled={Boolean(leftPane)}
                        leftPaneClosed={leftPaneClosed}
                        rightPaneClosed={rightPaneClosed}
                        onToggleRightPaneClose={this.onToggleRightPaneClose}
                        onToggleLeftPaneClose={this.onToggleLeftPaneClose}
                    />
                    <div className={classNames(Bem.cls(BLOCK, 'preview-container'), previewCls)}>{preview}</div>
                </div>
                <CreativeOptions
                    cls={classNames(Bem.cls(BLOCK, 'options', { closed: rightPaneClosed }), optionsCls)}
                    name={name}
                    onSaveName={onSaveName}
                    optionsConfig={optionsConfig}
                    optionsValue={optionsValue}
                    errors={errors}
                    onOptionChange={onOptionChange}
                    onGroupVisibleChange={onGroupVisibleChange}
                    tooltipsVisible={!rightPaneClosed}
                    isDocCenterTooltip={isDocCenterTooltip}
                />
                {this.renderGalleries(optionsConfig)}
            </>
        );
    }

    private onToggleRightPaneClose = (): void => {
        this.setState({
            ...this.state,
            rightPaneClosed: !this.state.rightPaneClosed
        });
    };

    private onToggleLeftPaneClose = (): void => {
        this.setState({
            ...this.state,
            leftPaneClosed: !this.state.leftPaneClosed
        });
    };

    private renderGalleries(config: CreativeOptionsConfig): React.ReactNode {
        const resources = getEnabledGalleriesFromConfig(config);

        return resources.map(resource => <GalleryRenderer key={resource} resourceType={resource} />);
    }

    private renderLeftPane = (): React.ReactNode => {
        const { leftPane } = this.props;
        const { leftPaneClosed } = this.state;

        if (!leftPane) {
            return null;
        }

        return (
            <div
                className={Bem.cls(BLOCK, 'left-pane', {
                    closed: Boolean(leftPaneClosed)
                })}
            >
                {leftPane}
            </div>
        );
    };
}
